import { router } from '@inertiajs/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as React from 'react';

import {
  AMBER,
  FONT_BODY,
  FONT_DISPLAY,
  Reveal,
  SectionHeading,
  TEAL,
  glassCardSx,
  gridLayerSx,
} from './LandingKit';

const steps = [
  {
    number: '1',
    title: 'O motorista instala o app',
    description: 'Login e pronto. O celular que ele já tem vira o rastreador da operação.',
  },
  {
    number: '2',
    title: 'Você acompanha na torre',
    description: 'Coletas, posições e status de cada visita ao vivo, direto no navegador.',
  },
  {
    number: '3',
    title: 'Pergunta no WhatsApp quando quiser',
    description: 'Resumo do dia, onde está um motorista, o que faltou coletar. Sem abrir o sistema.',
  },
];

export default function HowItWorks() {
  const handleGetStarted = () => {
    router.visit('/leads/new?source=landing-how-it-works');
  };

  return (
    <Box
      id="how"
      sx={(theme) => ({
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#eef3f2',
        ...theme.applyStyles('dark', { backgroundColor: '#05080b' }),
      })}
    >
      <Box aria-hidden sx={gridLayerSx} />
      <Container sx={{ position: 'relative', py: { xs: 10, sm: 18 } }}>
        <Reveal>
          <SectionHeading
            overline="// COMO FUNCIONA"
            title="Como funciona"
            subtitle="Sem hardware e sem implantação longa. Sua equipe começa em minutos."
            align="center"
          />
        </Reveal>

        <Box sx={{ position: 'relative', mt: { xs: 5, sm: 8 } }}>
          {/* dashed route connector behind the badges (desktop only) */}
          <Box
            aria-hidden
            component="svg"
            viewBox="0 0 1000 8"
            preserveAspectRatio="none"
            sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              top: 20,
              left: '16%',
              width: '68%',
              height: 8,
              pointerEvents: 'none',
            }}
          >
            <line
              x1="0"
              y1="4"
              x2="1000"
              y2="4"
              fill="none"
              stroke={TEAL}
              strokeOpacity="0.4"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="2 12"
            />
          </Box>

          <Grid container spacing={3}>
            {steps.map((step, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={step.number}>
                <Reveal delay={0.1 + index * 0.12} sx={{ height: '100%' }}>
                  <Stack
                    spacing={2}
                    useFlexGap
                    sx={(theme) => ({
                      ...glassCardSx(theme),
                      p: 3.5,
                      height: '100%',
                      alignItems: 'center',
                      textAlign: 'center',
                    })}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: FONT_DISPLAY,
                        fontWeight: 800,
                        fontSize: '1.5rem',
                        color: '#04110f',
                        background:
                          index % 2
                            ? `linear-gradient(180deg, ${AMBER}, #e8922a)`
                            : `linear-gradient(180deg, ${TEAL}, #00a594)`,
                        boxShadow:
                          index % 2 ? `0 10px 28px ${AMBER}55` : `0 10px 28px ${TEAL}55`,
                      }}
                    >
                      {step.number}
                    </Box>
                    <Typography
                      sx={(theme) => ({
                        fontFamily: FONT_DISPLAY,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '-0.005em',
                        fontSize: '1.05rem',
                        lineHeight: 1.15,
                        color: '#071513',
                        ...theme.applyStyles('dark', { color: '#f2fbf9' }),
                      })}
                    >
                      {step.title}
                    </Typography>
                    <Typography
                      sx={(theme) => ({
                        fontFamily: FONT_BODY,
                        fontSize: '0.95rem',
                        lineHeight: 1.5,
                        color: '#33514c',
                        ...theme.applyStyles('dark', { color: '#9fc4bd' }),
                      })}
                    >
                      {step.description}
                    </Typography>
                  </Stack>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Reveal delay={0.45}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 5, sm: 8 } }}>
            <Button
              onClick={handleGetStarted}
              disableElevation
              sx={{
                px: 3.5,
                py: 1.4,
                borderRadius: 999,
                fontFamily: FONT_BODY,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '1rem',
                color: '#04110f',
                background: `linear-gradient(180deg, ${TEAL}, #00a594)`,
                boxShadow: `0 10px 30px ${TEAL}40`,
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 16px 40px ${TEAL}66`,
                  background: `linear-gradient(180deg, ${TEAL}, #00a594)`,
                },
              }}
            >
              Agendar demonstração
            </Button>
          </Box>
        </Reveal>
      </Container>
    </Box>
  );
}
